import {useEffect, useState, useMemo} from 'react'
import {useNavigate} from 'react-router-dom'
import ApiService from '../../common/ApiCalls'
import {formatIconName, getIconIndex} from '../util/formatter'
import {useTheme} from '@mui/material/styles'
import { useDispatch } from 'react-redux';
import {
  useSettingsSelector,
  setGoogleImagePull,
  updateGoogleImagePull,
} from '../../v2/store/settings';
import { defaultExecutiveMarker } from '../GMap/executiveMarker';
import useEntity from '../../features/assets/resources/useEntity';

const useMapConfiguration = (onEditMode, setOnEditMode, setIsLoading, setIsSnackbarOpen) => {
    const navigate = useNavigate()
    const theme = useTheme()
    const dispatch = useDispatch();

    const { listEntityTypes, loadEntities } = useEntity();

    const { isGoogleImagePull } = useSettingsSelector();

    // Map Centralization
    const [selectedCountry, setSelectedCountry] = useState(null)
    const [selectedProvince, setSelectedProvince] = useState(null)
    const [selectedCountryOriginal, setSelectedCountryOriginal] = useState(null)
    const [selectedProvinceOriginal, setSelectedProvinceOriginal] = useState(null)
    const [mapZoomValue, setMapZoomValue] = useState(5)

    // Marker
    const [markerList, setMarkerList] = useState([])
    const [options, setOptions] = useState({'Default Marker': {icon: 23, color: theme.palette.primary.accent, style: 'proximity'}})
    const [industryTypeIcons, setIndustryTypeIcons] = useState([]);
    const [industryTypeList, setIndustryTypeList] = useState([]);
    const [entityTypeColors, setEntityTypeColors] = useState([]);
    const [entityTypeList, setEntityTypeList] = useState([]);

    const [executiveEntities, setExecutiveEntities] = useState([]);
    const [executiveEntityMarkers, setExecutiveEntityMarkers] = useState([]);

    const defaultMarker = useMemo(() => options['Default Marker'], [options]);

    const handleReset = () => {

    }

    const handlePreview = () => {
        if(selectedProvince){
            localStorage.setItem('mapCentralization', JSON.stringify(selectedProvince))
        }else if(selectedCountry){
            localStorage.setItem('mapCentralization', JSON.stringify(selectedCountry))
        }
        localStorage.setItem('previewMode', 'map')
        navigate('/')
        window.location.reload()
    }

    const handleCancel = () => {
        setOnEditMode(false)
    }

    const getIconId = (style, iconName) => {
        return markerList.find(marker => marker.icon === `${style}-${iconName}`)
    }

  const formatSelectedIconList = (allMarkers) => {
    return Object.values(allMarkers).map((marker) => {
      const iconId = getIconId(marker.style, marker.icon)?.icon_id || null;
      if (marker.entityId && !iconId) throw new Error('Executive marker icon is unavailable');
      return {
        ...(marker.entityId ? { entityId: marker.entityId } : {}),
        endCustomerId: marker.endCustomerId || null,
        color: marker.color || null,
        iconId,
        industryTypeId: marker.industryTypeId || null,
        entityTypeId: marker.entityTypeId || null,
      };
    });
  };

    const handleSave = async () => {
        setIsLoading(true)
        try{
            // Map Centralization
            if(selectedCountry){
                const selectedLocation = selectedProvince ? selectedProvince : selectedCountry
                await ApiService().setLandingOptionSystem(selectedLocation.id)
                selectedLocation.location_zoom = mapZoomValue

                localStorage.setItem('mapCentralization', JSON.stringify(selectedLocation))
            }
            // Google Image Pull
            if (typeof isGoogleImagePull === 'boolean') {
              dispatch(updateGoogleImagePull(isGoogleImagePull));
            }

            // Marker
            const industryTypeCollection = industryTypeIcons.reduce((acc, item) => {
              if (!item.id) return acc;
              acc[item.id] = {
                icon: item.icon ? formatIconName(item.icon) : null,
                style: (item.style === 'default' ? 'proximity' : item.style) || null,
                industryTypeId: item.id,
                color: item.color || null
              };
              return acc;
            }, {});

            const entityTypeCollection = entityTypeColors.reduce((acc, item) => {
              if (!item.id) return acc;
              acc[item.id] = {
                color: item.color || null,
                entityTypeId: item.id
              };
              return acc;
            }, {});

            const mainOptions = Object.keys(options).reduce((acc, key) => {
              const option = options[key];
              acc[key] = {
                endCustomerId: option.endCustomerId || null,
                color: option.color || null,
                style: (option.style === 'default' ? 'proximity' : option.style) || null,
                icon: formatIconName(option.icon) || null,
                industryTypeId: null,
                entityTypeId: null
              };
              return acc;
            }, {});

            const executiveEntityCollection = executiveEntityMarkers.reduce((acc, item) => {
              if (!item.id) return acc;
              const marker = { ...defaultExecutiveMarker, ...item };
              acc[item.id] = {
                entityId: item.id,
                color: marker.color || defaultExecutiveMarker.color,
                style: (marker.style === 'default' ? 'proximity' : marker.style) || defaultExecutiveMarker.style,
                icon: formatIconName(marker.icon ?? defaultExecutiveMarker.icon)
              };
              return acc;
            }, {});

            const allMarkers = { ...mainOptions, ...industryTypeCollection, ...entityTypeCollection };

            const iconList = formatSelectedIconList([...Object.values(allMarkers), ...Object.values(executiveEntityCollection)]);
            await ApiService().insertIconMapping(iconList);

            const iconMapping = {
              ...mainOptions,
              entityTypeColors: entityTypeCollection,
              industryTypeIcons: industryTypeCollection,
              executiveEntityMarkers: executiveEntityCollection
            };

            localStorage.setItem('iconMapping', JSON.stringify(iconMapping));
            setIsSnackbarOpen({ isOpen: true, severity: 'success', message: 'Information is successfully updated' });
            setIsLoading(false);
            setOnEditMode(false);

          } catch (e) {
            console.log(e);
            setIsSnackbarOpen({ isOpen: true, severity: 'error', message: 'Unable to save map configuration. Please try again.' });
            setIsLoading(false);
          }
    }

    const makeEndClientObject = (endClientArray) => {
        let formattedObject = {}
        for(let i = 0; i < endClientArray.length; i++){
            formattedObject = {...formattedObject, [endClientArray[i].name]: {icon: 23, color: theme.palette.primary.accent, style: 'proximity', endCustomerId: endClientArray[i].end_customer_id}}
        }
        formattedObject = {...formattedObject}
        return formattedObject
    }

    const setSelectedMarkerFromLocalStorage = (settings) => {
      const iconMapping = localStorage.getItem('iconMapping');
      if (iconMapping) {
        const iconMappingObject = JSON.parse(iconMapping);

        setExecutiveEntityMarkers(Object.entries(iconMappingObject.executiveEntityMarkers || {})
          .map(([id, marker]) => ({ ...marker, id, icon: getIconIndex(marker.icon) })));

        const industryTypeIconsList = Object.entries(iconMappingObject?.industryTypeIcons || {})
          .map(([key, value]) => ({
            id: key,
            icon: getIconIndex(value?.icon || 'default'),
            style: value?.style,
            color: value?.color,
          }));
        setIndustryTypeIcons(industryTypeIconsList);

        const entityTypeColorsList = Object.entries(iconMappingObject?.entityTypeColors || {})
          .map(([key, value]) => ({
            id: key,
            color: value?.color
          }));
        setEntityTypeColors(entityTypeColorsList);

        const settingsStateCopy = Object.keys(settings).reduce((acc, key) => {
          acc[key] = iconMappingObject[key]
            ? {
              ...settings[key],
              color: iconMappingObject[key].color,
              style: iconMappingObject[key].style,
              icon: getIconIndex(iconMappingObject[key].icon)
            }
            : settings[key];
          return acc;
        }, {});
        setOptions(settingsStateCopy);
      } else {
        setExecutiveEntityMarkers([]);
        setOptions(settings);
      }
    };

    useEffect(()=>{
        const temp = localStorage.getItem('mapCentralization')
        if(temp){
            // setOnEditMode(true)
            // if(temp.location_name === 'Canada'){
            //     setSelectedCountry(selectedCountryOriginal)
            // }else{
            //     setSelectedCountry(selectedCountryOriginal)
            //     setSelectedProvince(selectedProvinceOriginal)
            // }
            // localStorage.removeItem('mapCentralization')
        }

        const getClientOptions = async () => {
            const endCustomers = await ApiService().getEndCustomers()
            const endCustomerArray = makeEndClientObject(endCustomers)
            const mergedOptionsObject = Object.assign({...options}, endCustomerArray)
            setSelectedMarkerFromLocalStorage(mergedOptionsObject)
        }
        getClientOptions()


        const getIconList = async () => {
            const iconList = await ApiService().getAllIcons()
            setMarkerList(iconList)
        }
        getIconList()

        const getIndustryTypeList = async () => {
          const industryTypes = await ApiService().getIndustries();
          const formattedIndustryTypes = industryTypes
            .filter((item) => (item.name && item.id))
            .map((item) => ({
              id: item.id,
              name: item.name
            }));
          setIndustryTypeList(formattedIndustryTypes);

        };
        getIndustryTypeList();

        const getEntityTypeList = async () => {
          const entityTypes = await listEntityTypes();
          const formattedEntityTypes = entityTypes
            .filter((item) => (item.name && item.id))
            .map((item) => ({
              id: item.id,
              name: item.name
            }));
          setEntityTypeList(formattedEntityTypes);
        };
        getEntityTypeList();

        const getExecutiveEntities = async () => {
          try {
            const entities = [];
            let page = 0;
            let batch;
            do {
              const response = await loadEntities({ sort: '+name', page, per_page: 100 });
              if (response.error) throw response.error;
              batch = response.data?.data || [];
              entities.push(...batch);
              page += 1;
            } while (batch.length === 100);
            setExecutiveEntities(entities);
          } catch (error) {
            setIsSnackbarOpen({ isOpen: true, severity: 'error', message: 'Unable to load entities for executive markers.' });
          }
        };
        getExecutiveEntities();
    // XXX Fix this in the future
    // eslint-disable-next-line
    }, [])

    useEffect(()=>{
      if (!onEditMode) {
        const getSelectedLocation = async () => {
          const selectedLocation = await ApiService().getLandingOptionSystem()
          dispatch(setGoogleImagePull(selectedLocation.google_image_pull));

          if(selectedLocation?.is_country || selectedLocation?.location_name?.toLocaleLowerCase()==='canada'){
            setSelectedCountry(selectedLocation)
            setSelectedCountryOriginal(selectedLocation)
            setSelectedProvince(null)
            setSelectedProvinceOriginal(null)
          }else{
            setSelectedCountry({location_name: 'canada'})
            setSelectedCountryOriginal({location_name: 'canada'})
            setSelectedProvince(selectedLocation)
            setSelectedProvinceOriginal(selectedLocation)
          }
        }
        getSelectedLocation()
        setSelectedMarkerFromLocalStorage(options)

        const mapCentralization = JSON.parse(window.localStorage.getItem('mapCentralization'));
        setMapZoomValue(mapCentralization?.location_zoom || 5);
      }
    // XXX Fix this in the future
    // eslint-disable-next-line
    }, [onEditMode])


    return {selectedCountry, setSelectedCountry, selectedProvince, setSelectedProvince, handleReset, handlePreview,
        handleCancel, handleSave, selectedCountryOriginal, setSelectedCountryOriginal, selectedProvinceOriginal,
        setSelectedProvinceOriginal, options, setOptions, mapZoomValue, setMapZoomValue,
        executiveEntities, executiveEntityMarkers, setExecutiveEntityMarkers,
        industryTypeList, entityTypeList, industryTypeIcons, setIndustryTypeIcons, entityTypeColors, setEntityTypeColors, defaultMarker}
}

export default useMapConfiguration
